import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import TrendingDownIcon from 'components/material-symbol/icons/TrendingDown'
import TrendingUpIcon from 'components/material-symbol/icons/TrendingUp'
import TrophyIcon from 'components/material-symbol/icons/Trophy'
import {
  BENCHMARK_PALETTE,
  DASHBOARD_BODY_TEXT,
  DASHBOARD_SURFACE_MUTED,
  PGA_COACH_ERROR_BG,
  PGA_COACH_ERROR_TEXT,
  PGA_COACH_SUCCESS_BG,
  PGA_COACH_SUCCESS_TEXT,
} from 'pages/pga-coach/dashboard/dashboard-colors'
import {
  BENCHMARK_STATUS,
  getBenchmarkStatusLabel,
  getBenchmarkStatusTier,
} from './utils'

export const BENCHMARK_COLORS = BENCHMARK_PALETTE

export const BENCHMARK_STATUS_CHIP_COLORS = {
  [BENCHMARK_STATUS.TOP_10]: {
    backgroundColor: BENCHMARK_COLORS.top10.chipBackground,
    color: BENCHMARK_COLORS.top10.chipText,
  },
  [BENCHMARK_STATUS.ABOVE_MEDIAN]: {
    backgroundColor: BENCHMARK_COLORS.success.chipBackground,
    color: BENCHMARK_COLORS.success.chipText,
  },
  [BENCHMARK_STATUS.AT_MEDIAN]: {
    backgroundColor: BENCHMARK_COLORS.neutral.chipBackground,
    color: BENCHMARK_COLORS.neutral.chipText,
  },
  [BENCHMARK_STATUS.BELOW_MEDIAN]: {
    backgroundColor: BENCHMARK_COLORS.neutral.chipBackground,
    color: BENCHMARK_COLORS.neutral.chipText,
  },
}

export const getBenchmarkStatusChipColors = tier =>
  BENCHMARK_STATUS_CHIP_COLORS[tier] ??
  BENCHMARK_STATUS_CHIP_COLORS[BENCHMARK_STATUS.BELOW_MEDIAN]

export const BENCHMARK_BAR_FILL_COLORS = {
  [BENCHMARK_STATUS.TOP_10]: BENCHMARK_COLORS.top10.barFill,
  [BENCHMARK_STATUS.ABOVE_MEDIAN]: BENCHMARK_COLORS.success.barFill,
  [BENCHMARK_STATUS.AT_MEDIAN]: BENCHMARK_COLORS.neutral.barFill,
  [BENCHMARK_STATUS.BELOW_MEDIAN]: BENCHMARK_COLORS.neutral.barFill,
}

export const getBenchmarkBarFillColor = tier =>
  BENCHMARK_BAR_FILL_COLORS[tier] ??
  BENCHMARK_BAR_FILL_COLORS[BENCHMARK_STATUS.BELOW_MEDIAN]

const useStyles = makeStyles(() => ({
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    gap: 4,
    borderRadius: 9999,
    padding: '2px 8px',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '16px',
    whiteSpace: 'nowrap',
    '& svg, & .MuiSvgIcon-root': {
      fontSize: 14,
      flexShrink: 0,
    },
  },
  top10: BENCHMARK_STATUS_CHIP_COLORS[BENCHMARK_STATUS.TOP_10],
  aboveMedian: BENCHMARK_STATUS_CHIP_COLORS[BENCHMARK_STATUS.ABOVE_MEDIAN],
  atMedian: BENCHMARK_STATUS_CHIP_COLORS[BENCHMARK_STATUS.AT_MEDIAN],
  belowMedian: BENCHMARK_STATUS_CHIP_COLORS[BENCHMARK_STATUS.BELOW_MEDIAN],
  deltaPositive: {
    backgroundColor: PGA_COACH_SUCCESS_BG,
    color: PGA_COACH_SUCCESS_TEXT,
  },
  deltaNegative: {
    backgroundColor: PGA_COACH_ERROR_BG,
    color: PGA_COACH_ERROR_TEXT,
  },
  deltaNeutral: {
    backgroundColor: DASHBOARD_SURFACE_MUTED,
    color: DASHBOARD_BODY_TEXT,
  },
}))

const STATUS_ICONS = {
  [BENCHMARK_STATUS.TOP_10]: TrophyIcon,
  [BENCHMARK_STATUS.AT_MEDIAN]: TrendingUpIcon,
  [BENCHMARK_STATUS.ABOVE_MEDIAN]: TrendingUpIcon,
  [BENCHMARK_STATUS.BELOW_MEDIAN]: TrendingDownIcon,
}

export const BenchmarkStatusChip = ({ metric, className }) => {
  const classes = useStyles()
  const tier = getBenchmarkStatusTier(metric)
  const StatusIcon = STATUS_ICONS[tier]

  return (
    <span
      className={clsx(classes.chip, className, {
        [classes.top10]: tier === BENCHMARK_STATUS.TOP_10,
        [classes.aboveMedian]: tier === BENCHMARK_STATUS.ABOVE_MEDIAN,
        [classes.atMedian]: tier === BENCHMARK_STATUS.AT_MEDIAN,
        [classes.belowMedian]: tier === BENCHMARK_STATUS.BELOW_MEDIAN,
      })}
    >
      <StatusIcon aria-hidden />
      {getBenchmarkStatusLabel(tier)}
    </span>
  )
}

BenchmarkStatusChip.propTypes = {
  metric: PropTypes.shape({
    coachValue: PropTypes.number.isRequired,
    medianValue: PropTypes.number.isRequired,
    top10Value: PropTypes.number.isRequired,
    higherIsBetter: PropTypes.bool.isRequired,
  }).isRequired,
  className: PropTypes.string,
}

export const BenchmarkDeltaChip = ({
  label,
  positive = false,
  neutral = false,
  className,
}) => {
  const classes = useStyles()

  if (!label) {
    return null
  }

  return (
    <span
      className={clsx(classes.chip, className, {
        [classes.deltaPositive]: positive,
        [classes.deltaNegative]: !positive && !neutral,
        [classes.deltaNeutral]: neutral,
      })}
    >
      {label}
    </span>
  )
}

BenchmarkDeltaChip.propTypes = {
  label: PropTypes.string,
  positive: PropTypes.bool,
  neutral: PropTypes.bool,
  className: PropTypes.string,
}
